import { NavLink } from 'react-router';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { usePlatformSidebarCollapsed } from '@/platform/components/platform-sidebar-context';

export type ProductSideNavItem = {
  to: string;
  label: string;
  end?: boolean;
  /** Primary destinations: shown as pictogram when the rail is collapsed. */
  icon?: ReactNode;
};

function navInitial(label: string): string {
  const first = label.trim().charAt(0);
  return first ? first.toLocaleUpperCase('de-DE') : '?';
}

/** Flat product links in the platform sidebar. Replaces OverflowTabNav for module chrome. */
export function ProductSideNav({
  ariaLabel,
  items,
  onNavigate,
}: {
  ariaLabel: string;
  items: readonly ProductSideNavItem[];
  onNavigate?: () => void;
}) {
  const collapsed = usePlatformSidebarCollapsed();
  if (items.length === 0) {
    return null;
  }

  return (
    <nav aria-label={ariaLabel} className="space-y-0.5">
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              aria-label={item.label}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'sidebar-hit flex min-h-9 items-center gap-2 rounded-md px-3 text-sm whitespace-nowrap text-muted-foreground transition-colors hover:bg-accent hover:text-primary',
                  isActive && 'bg-accent font-medium text-primary',
                )
              }
            >
              {item.icon ? (
                <span className="inline-flex size-4 shrink-0 items-center justify-center text-current [&_svg]:size-4">
                  {item.icon}
                </span>
              ) : (
                <span
                  aria-hidden
                  className="sidebar-mark flex size-4 shrink-0 items-center justify-center text-[0.7rem] font-semibold tracking-wide"
                >
                  {navInitial(item.label)}
                </span>
              )}
              <span className="sidebar-copy truncate">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
